import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, ChartConfiguration } from 'chart.js';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { BaseChartDirective } from 'ng2-charts';
import { Subject, takeUntil } from 'rxjs';

import { PeopleTypeData } from '@app/admin/statistics/statistics.model';
import { NumberFormatPipe } from '@shared/pipes/number-format.pipe';
import { PEOPLE_COLORS } from '@app/admin/statistics/statistics.constants';

@Component({
  selector: 'flow-age-gender-distribution-doughnut-chart',
  templateUrl: './age-gender-distribution-doughnut-chart.component.html',
  styleUrls: ['./age-gender-distribution-doughnut-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgeGenderDistributionDoughnutChartComponent implements OnChanges, OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  @Input() data!: PeopleTypeData;
  @Input() dataCompare!: PeopleTypeData;
  @Input() weekNumberCompare!: number;
  @Input() weekNumber!: number;
  @Input() compare!: boolean;
  @Input() timeframeType!: string;
  @Input() hasData: boolean = false;

  chartPlugins = [DataLabelsPlugin];
  doughnutChartData: ChartData<'doughnut', number[], string | string[]> = null;
  doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    clip: 90,
    plugins: {
      legend: {
        display: false,
      },
    },
  };
  chartDoughnutPlugins = [
    {
      id: 'centerTextPlugin',
      beforeDraw: (chart: any) => {
        const ctx = chart.ctx;
        const width = chart.width;
        const height = chart.height;
        ctx.restore();

        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#202020';

        ctx.font = 'bold 1.125rem Inter';
        ctx.fillText(this.totalAllCount, width / 2, height / 2 - 8);

        ctx.font = '0.875rem Inter';
        ctx.fillText(this.translateService.instant('global.total').toString(), width / 2, height / 2 + 12);

        ctx.save();
      },
    },
  ];

  totalAllCount: string = '0';

  constructor(private translateService: TranslateService, private numberFormatPipe: NumberFormatPipe) {}

  ngOnInit() {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.chart) {
        this.setDoughnutChart(this.data, this.dataCompare);
        this.chart.update();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = changes.data?.currentValue;
    const currentValueCompare = changes.dataCompare?.currentValue;

    if (currentValue) {
      this.setDoughnutChart(currentValue, currentValueCompare || this.dataCompare);
    }
  }

  setDoughnutChart(data: PeopleTypeData, dataCompare: PeopleTypeData) {
    const labelMen = this.translateService.instant('peopleCount.men');
    const labelWomen = this.translateService.instant('peopleCount.women');
    const labelMenToCompare = `${this.translateService.instant('peopleCount.men')}, ${this.translateService.instant('peopleCount.week')} ${
      this.weekNumberCompare
    }`;
    const labelWomenToCompare = `${this.translateService.instant('peopleCount.women')}, ${this.translateService.instant(
      'peopleCount.week'
    )} ${this.weekNumberCompare}`;

    const total = data?.totalMen + data?.totalWomen + (dataCompare?.totalMen + dataCompare?.totalWomen || 0);
    this.totalAllCount = this.numberFormatPipe.transform(total);

    const totalMen = data?.totalMen;
    const totalMenCompare = dataCompare?.totalMen || 0;

    const totalWomen = data?.totalWomen;
    const totalWomenCompare = dataCompare?.totalWomen || 0;

    this.doughnutChartData = {
      labels: [labelWomen, labelMen, ...(this.compare ? [labelWomenToCompare, labelMenToCompare] : [])],
      datasets: [
        {
          data: [totalWomen, totalMen, ...(this.compare ? [totalWomenCompare, totalMenCompare] : [])],
          backgroundColor: [PEOPLE_COLORS.women, PEOPLE_COLORS.men, PEOPLE_COLORS.womenCompare, PEOPLE_COLORS.menCompare],
          hoverOffset: 4,
        },
      ],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
